import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { get, ref } from 'firebase/database';
import toast from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
    ChevronDown,
    ChevronRight,
    ExternalLink,
    FileWarning,
    Inbox,
    Paperclip,
    RefreshCcw,
    Search,
} from 'lucide-react';

import { db } from '../../utils/firebaseApp.js';

const INBOUND_EMAILS_PATH = 'inboundEmails';
const REJECTED_STATUSES = new Set(['rejected_sender', 'unrouted', 'disabled_inbox']);

const STATUS_STYLES = {
    received: 'bg-green-100 text-green-800',
    uploaded: 'bg-green-100 text-green-800',
    rejected_sender: 'bg-red-100 text-red-800',
    unrouted: 'bg-orange-100 text-orange-800',
    disabled_inbox: 'bg-amber-100 text-amber-800',
    no_supported_files: 'bg-yellow-100 text-yellow-800',
    credits_failed: 'bg-red-100 text-red-800',
    extraction_missing: 'bg-orange-100 text-orange-800',
    processing_failed: 'bg-red-100 text-red-800',
};

const TABS = [
    { id: 'extraction', label: 'Extraction emails' },
    { id: 'custom', label: 'Custom projects' },
    { id: 'rejected', label: 'Rejected' },
];

function toList(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object');
    return Object.values(value).filter((item) => item && typeof item === 'object');
}

function formatLocalDateTime(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

function formatBytes(bytes) {
    const size = Number(bytes);
    if (!Number.isFinite(size) || size < 0) return '—';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatHeaderValue(value) {
    if (Array.isArray(value)) return value.join(', ');
    if (value == null) return '';
    return String(value);
}

function buildHtmlDocument(html) {
    if (!html) return '';
    if (/<html[\s>]/i.test(html)) return html;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{margin:16px;font-family:system-ui,-apple-system,sans-serif;color:#172033;word-break:break-word;}</style></head><body>${html}</body></html>`;
}

function inferKind(raw) {
    if (raw.kind === 'extraction' || raw.kind === 'client' || raw.kind === 'unrouted') {
        return raw.kind;
    }
    const inboxId = String(raw.inboxId || '').toLowerCase();
    const localPart = String(raw.recipientLocalPart || '').toLowerCase();
    if (inboxId.startsWith('ex_') || localPart.startsWith('ex_') || raw.uid || raw.extractionId) {
        return 'extraction';
    }
    if (raw.status === 'unrouted') {
        return 'unrouted';
    }
    if (inboxId || localPart) {
        return 'client';
    }
    return 'unrouted';
}

function normalizeEmail(inboundId, raw) {
    const attachments = toList(raw.attachments);
    const rejectedAttachments = toList(raw.rejectedAttachments);
    const kind = inferKind(raw);
    const status = raw.status || '';
    const hasBody = Boolean(raw.bodyPlain || raw.bodyHtml || raw.strippedText || raw.strippedHtml);
    const contentStored = raw.contentStored === false
        ? false
        : raw.contentStored === true
            ? true
            : hasBody || attachments.some((file) => file.url || file.storagePath);

    return {
        inboundId: raw.inboundId || inboundId,
        receivedAt: raw.receivedAt || '',
        status,
        kind,
        inboxId: raw.inboxId || raw.recipientLocalPart || '',
        uid: raw.uid || '',
        extractionId: raw.extractionId || '',
        batchId: raw.batchId || '',
        recipient: raw.recipient || '',
        recipientLocalPart: raw.recipientLocalPart || '',
        sender: raw.sender || '',
        from: raw.from || '',
        fromEmail: raw.fromEmail || '',
        replyTo: raw.replyTo || '',
        subject: raw.subject || '',
        messageId: raw.messageId || '',
        inReplyTo: raw.inReplyTo || '',
        references: raw.references || '',
        date: raw.date || '',
        bodyPlain: raw.bodyPlain || '',
        strippedText: raw.strippedText || '',
        bodyHtml: raw.bodyHtml || '',
        strippedHtml: raw.strippedHtml || '',
        headers: raw.headers && typeof raw.headers === 'object' ? raw.headers : {},
        contentIdMap: raw.contentIdMap || null,
        attachmentCount: Number.isFinite(Number(raw.attachmentCount))
            ? Number(raw.attachmentCount)
            : attachments.length,
        attachments,
        rejectedAttachments,
        uploadedFiles: toList(raw.uploadedFiles),
        uploadError: raw.uploadError || '',
        contentStored,
        isRejected: REJECTED_STATUSES.has(status),
    };
}

function StatusBadge({ status }) {
    const styles = STATUS_STYLES[status] || 'bg-gray-100 text-gray-700';
    return (
        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${styles}`}>
            {status || 'unknown'}
        </span>
    );
}

function MetaField({ label, value }) {
    return (
        <div className="min-w-0">
            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</dt>
            <dd className="mt-1 break-words text-sm text-slate-800">{value || '—'}</dd>
        </div>
    );
}

function MonoLink({ href, children }) {
    if (!href) return <span className="font-mono text-sm text-slate-800">{children || '—'}</span>;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-sm text-indigo-600 hover:text-indigo-500"
            onClick={(event) => event.stopPropagation()}
        >
            {children}
            <ExternalLink className="h-3.5 w-3.5" />
        </a>
    );
}

function EmailDetail({ email, showExtractionLinks }) {
    const [showHtml, setShowHtml] = useState(false);
    const [headersOpen, setHeadersOpen] = useState(false);

    const textBody = email.strippedText || email.bodyPlain;
    const htmlBody = email.strippedHtml || email.bodyHtml;
    const htmlDocument = useMemo(() => buildHtmlDocument(htmlBody), [htmlBody]);
    const headerEntries = useMemo(() => Object.entries(email.headers || {}), [email.headers]);
    const extractionHref = email.uid && email.extractionId
        ? `/extractions/${email.extractionId}?adminUserId=${email.uid}`
        : '';
    const batchHref = email.uid && email.extractionId && email.batchId
        ? `/extractions/${email.extractionId}/batch/${email.batchId}?adminUserId=${email.uid}`
        : '';

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <MetaField label="From" value={email.from || email.fromEmail} />
                    <MetaField label="From email" value={email.fromEmail} />
                    <MetaField label="Reply-To" value={email.replyTo} />
                    <MetaField label="Recipient" value={email.recipient} />
                    <MetaField label="Inbox" value={email.inboxId || email.recipientLocalPart} />
                    <MetaField label="Kind" value={email.kind} />
                    <MetaField label="Subject" value={email.subject} />
                    <MetaField label="Message-ID" value={email.messageId} />
                    <MetaField label="Status" value={email.status} />
                    <MetaField label="Received at" value={formatLocalDateTime(email.receivedAt)} />
                    {showExtractionLinks && (
                        <>
                            <div className="min-w-0">
                                <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">User ID</dt>
                                <dd className="mt-1 break-words text-sm text-slate-800">{email.uid || '—'}</dd>
                            </div>
                            <div className="min-w-0">
                                <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Extraction</dt>
                                <dd className="mt-1">
                                    {email.extractionId ? (
                                        <MonoLink href={extractionHref}>{email.extractionId}</MonoLink>
                                    ) : '—'}
                                </dd>
                            </div>
                            <div className="min-w-0">
                                <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Batch</dt>
                                <dd className="mt-1">
                                    {email.batchId ? (
                                        <MonoLink href={batchHref}>{email.batchId}</MonoLink>
                                    ) : '—'}
                                </dd>
                            </div>
                        </>
                    )}
                </dl>

                {email.uploadError && (
                    <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{email.uploadError}</p>
                )}
            </div>

            {!email.contentStored ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                    <h3 className="text-lg font-semibold text-amber-900">Body and files were not stored</h3>
                    <p className="mt-2 text-sm text-amber-800">
                        Rejected, unrouted, and disabled-inbox messages only keep minimal metadata
                        (from, subject, recipient, attachment names). The email body and file contents
                        are not saved.
                    </p>
                    {email.attachments.length > 0 && (
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-900">
                            {email.attachments.map((attachment, index) => (
                                <li key={`${attachment.filename || 'file'}-${index}`}>
                                    {attachment.filename || 'unnamed'}
                                    {attachment.size ? ` · ${formatBytes(attachment.size)}` : ''}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : (
                <>
                    <div className="rounded-xl border border-slate-100 bg-white p-5">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <h3 className="text-lg font-semibold text-slate-900">Body</h3>
                            <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                                <button
                                    type="button"
                                    onClick={() => setShowHtml(false)}
                                    className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                                        !showHtml ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    Text
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowHtml(true)}
                                    disabled={!htmlBody}
                                    className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                                        showHtml ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                                    } disabled:cursor-not-allowed disabled:opacity-40`}
                                >
                                    HTML
                                </button>
                            </div>
                        </div>

                        {showHtml ? (
                            htmlBody ? (
                                <iframe
                                    title="Inbound email HTML"
                                    sandbox=""
                                    referrerPolicy="no-referrer"
                                    srcDoc={htmlDocument}
                                    className="h-[360px] w-full rounded-lg border border-slate-200 bg-white"
                                />
                            ) : (
                                <p className="text-sm text-slate-500">No HTML body on this message.</p>
                            )
                        ) : (
                            <pre className="max-h-[360px] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-4 text-sm text-slate-800">
                                {textBody || 'No text body on this message.'}
                            </pre>
                        )}
                    </div>

                    <div className="rounded-xl border border-slate-100 bg-white p-5">
                        <div className="mb-4 flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-slate-500" />
                            <h3 className="text-lg font-semibold text-slate-900">
                                Attachments
                                <span className="ml-2 text-sm font-medium text-slate-500">
                                    {email.attachments.length}
                                </span>
                            </h3>
                        </div>

                        {email.attachments.length === 0 ? (
                            <p className="text-sm text-slate-500">No accepted attachments.</p>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {email.attachments.map((attachment, index) => (
                                    <li
                                        key={`${attachment.fieldname || attachment.filename || index}-${index}`}
                                        className="flex flex-wrap items-center justify-between gap-3 py-3"
                                    >
                                        <div className="min-w-0">
                                            <div className="truncate text-sm font-semibold text-slate-800">
                                                {attachment.filename || 'unnamed'}
                                            </div>
                                            <div className="mt-0.5 text-xs text-slate-500">
                                                {formatBytes(attachment.size)}
                                                {attachment.mimetype ? ` · ${attachment.mimetype}` : ''}
                                            </div>
                                        </div>
                                        {attachment.url ? (
                                            <a
                                                href={attachment.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                                            >
                                                Open
                                                <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        ) : (
                                            <span className="text-xs text-slate-400">No URL</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {email.uploadedFiles.length > 0 && (
                        <div className="rounded-xl border border-slate-100 bg-white p-5">
                            <h3 className="mb-3 text-lg font-semibold text-slate-900">Uploaded files</h3>
                            <ul className="divide-y divide-slate-100">
                                {email.uploadedFiles.map((file, index) => (
                                    <li key={`${file.fileId || file.fileName || index}-uploaded`} className="py-3 text-sm">
                                        <span className="font-semibold text-slate-800">{file.fileName || file.fileId || 'file'}</span>
                                        {file.numberOfPages ? (
                                            <span className="ml-2 text-xs text-slate-500">{file.numberOfPages} pages</span>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {email.rejectedAttachments.length > 0 && (
                <div className="rounded-xl border border-red-100 bg-white p-5">
                    <div className="mb-4 flex items-center gap-2">
                        <FileWarning className="h-4 w-4 text-red-500" />
                        <h3 className="text-lg font-semibold text-slate-900">
                            Rejected attachments
                            <span className="ml-2 text-sm font-medium text-red-600">
                                {email.rejectedAttachments.length}
                            </span>
                        </h3>
                    </div>
                    <ul className="divide-y divide-slate-100">
                        {email.rejectedAttachments.map((attachment, index) => (
                            <li
                                key={`${attachment.fieldname || attachment.filename || index}-rejected-${index}`}
                                className="py-3"
                            >
                                <div className="text-sm font-semibold text-slate-800">
                                    {attachment.filename || 'unnamed'}
                                </div>
                                <div className="mt-0.5 text-xs text-slate-500">
                                    {formatBytes(attachment.size)}
                                    {attachment.mimetype ? ` · ${attachment.mimetype}` : ''}
                                </div>
                                {attachment.reason && (
                                    <div className="mt-1 text-xs font-medium text-red-600">{attachment.reason}</div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {email.contentStored && (
                <div className="rounded-xl border border-slate-100 bg-white">
                    <button
                        type="button"
                        onClick={() => setHeadersOpen((open) => !open)}
                        className="flex w-full items-center justify-between px-5 py-4 text-left"
                    >
                        <span className="text-sm font-semibold text-slate-900">Raw headers</span>
                        {headersOpen ? (
                            <ChevronDown className="h-4 w-4 text-slate-500" />
                        ) : (
                            <ChevronRight className="h-4 w-4 text-slate-500" />
                        )}
                    </button>
                    {headersOpen && (
                        <div className="border-t border-slate-100 px-5 pb-5">
                            {headerEntries.length === 0 ? (
                                <p className="pt-4 text-sm text-slate-500">No headers stored.</p>
                            ) : (
                                <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-4 font-mono text-xs text-slate-700">
                                    {headerEntries
                                        .map(([name, value]) => `${name}: ${formatHeaderValue(value)}`)
                                        .join('\n')}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function InboundEmails() {
    const [emails, setEmails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const [tab, setTab] = useState('extraction');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [inboxFilter, setInboxFilter] = useState('all');
    const [rejectedFilter, setRejectedFilter] = useState('all');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 250);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchEmails = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const snapshot = await get(ref(db, INBOUND_EMAILS_PATH));
            if (!snapshot.exists()) {
                setEmails([]);
                return;
            }

            const data = snapshot.val() || {};
            const list = Object.entries(data).map(([inboundId, raw]) => normalizeEmail(inboundId, raw || {}));
            list.sort((a, b) => {
                const timeA = new Date(a.receivedAt).getTime();
                const timeB = new Date(b.receivedAt).getTime();
                return (Number.isNaN(timeB) ? 0 : timeB) - (Number.isNaN(timeA) ? 0 : timeA);
            });
            setEmails(list);
        } catch (err) {
            const message = err?.code === 'PERMISSION_DENIED'
                ? 'Permission denied reading inbound emails.'
                : (err?.message || 'Failed to load inbound emails');
            setError(message);
            toast.error(message);
            setEmails([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEmails();
    }, [fetchEmails]);

    const customInboxes = useMemo(() => {
        const ids = new Set(
            emails.filter((email) => email.kind === 'client').map((email) => email.inboxId || email.recipientLocalPart)
        );
        return Array.from(ids).filter(Boolean).sort();
    }, [emails]);

    const statusOptions = useMemo(() => {
        if (tab === 'rejected') {
            return ['rejected_sender', 'unrouted', 'disabled_inbox'];
        }
        if (tab === 'custom') {
            return ['received', 'processing_failed', 'rejected_sender'];
        }
        return [
            'received',
            'uploaded',
            'no_supported_files',
            'credits_failed',
            'extraction_missing',
            'processing_failed',
            'rejected_sender',
            'disabled_inbox',
        ];
    }, [tab]);

    const filteredEmails = useMemo(() => {
        return emails.filter((email) => {
            if (tab === 'extraction' && email.kind !== 'extraction') return false;
            if (tab === 'custom' && email.kind !== 'client') return false;
            if (tab === 'rejected' && !email.isRejected) return false;

            if (tab === 'custom' && inboxFilter !== 'all') {
                const inbox = (email.inboxId || email.recipientLocalPart || '').toLowerCase();
                if (inbox !== inboxFilter.toLowerCase()) return false;
            }

            if (tab === 'rejected' && rejectedFilter !== 'all' && email.status !== rejectedFilter) return false;
            if (statusFilter !== 'all' && email.status !== statusFilter) return false;

            if (!debouncedSearch) return true;
            const haystack = [
                email.fromEmail,
                email.subject,
                email.recipient,
                email.uid,
                email.extractionId,
                email.batchId,
                email.inboxId,
            ].join(' ').toLowerCase();
            return haystack.includes(debouncedSearch);
        });
    }, [emails, tab, inboxFilter, rejectedFilter, statusFilter, debouncedSearch]);

    const selectedEmail = useMemo(
        () => emails.find((email) => email.inboundId === selectedId) || null,
        [emails, selectedId],
    );

    useEffect(() => {
        setStatusFilter('all');
        setSelectedId(null);
    }, [tab]);

    const closeDetail = () => setSelectedId(null);
    const showExtractionColumns = tab === 'extraction' || tab === 'rejected';

    return (
        <div className="space-y-4 pt-4">
            <div className="flex flex-wrap gap-2">
                {TABS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => setTab(item.id)}
                        className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                            tab === item.id
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <div className="flex flex-col gap-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative min-w-0 flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Search from, subject, recipient, uid, extraction"
                            className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                        className="block rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                        <option value="all">All statuses</option>
                        {statusOptions.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                    {tab === 'custom' && (
                        <select
                            value={inboxFilter}
                            onChange={(event) => setInboxFilter(event.target.value)}
                            className="block rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                            <option value="all">All inboxes</option>
                            {customInboxes.map((inbox) => (
                                <option key={inbox} value={inbox}>{inbox}</option>
                            ))}
                        </select>
                    )}
                    {tab === 'rejected' && (
                        <select
                            value={rejectedFilter}
                            onChange={(event) => setRejectedFilter(event.target.value)}
                            className="block rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                            <option value="all">All rejected</option>
                            <option value="rejected_sender">rejected_sender</option>
                            <option value="unrouted">unrouted</option>
                            <option value="disabled_inbox">disabled_inbox</option>
                        </select>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">
                        {filteredEmails.length} of {emails.length}
                    </span>
                    <button
                        type="button"
                        onClick={fetchEmails}
                        disabled={isLoading}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                            isLoading
                                ? 'cursor-not-allowed bg-slate-200 text-slate-400'
                                : 'bg-indigo-600 text-white hover:bg-indigo-500'
                        }`}
                    >
                        <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                        {isLoading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
                    {error}
                </div>
            )}

            {isLoading && emails.length === 0 && (
                <div className="space-y-3">
                    {[1, 2, 3].map((item) => (
                        <div key={item} className="animate-pulse rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
                            <div className="mb-3 h-4 w-1/3 rounded bg-slate-200" />
                            <div className="h-4 w-2/3 rounded bg-slate-100" />
                        </div>
                    ))}
                </div>
            )}

            {!isLoading && !error && filteredEmails.length === 0 && (
                <div className="rounded-xl border border-slate-100 bg-white p-12 text-center shadow-sm">
                    <Inbox className="mx-auto h-10 w-10 text-slate-400" />
                    <h3 className="mt-4 text-lg font-medium text-slate-900">No inbound emails</h3>
                    <p className="mt-2 text-sm text-slate-500">
                        {emails.length === 0
                            ? 'Nothing has been received at this path yet.'
                            : 'No messages match the current filters.'}
                    </p>
                </div>
            )}

            {filteredEmails.length > 0 && (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    Received
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Status
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    From
                                </th>
                                {tab === 'custom' && (
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Inbox
                                    </th>
                                )}
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Subject
                                </th>
                                {showExtractionColumns && (
                                    <>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            User
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Extraction
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Batch
                                        </th>
                                    </>
                                )}
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Files
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {filteredEmails.map((email) => {
                                const extractionHref = email.uid && email.extractionId
                                    ? `/extractions/${email.extractionId}?adminUserId=${email.uid}`
                                    : '';
                                const batchHref = email.uid && email.extractionId && email.batchId
                                    ? `/extractions/${email.extractionId}/batch/${email.batchId}?adminUserId=${email.uid}`
                                    : '';

                                return (
                                    <tr
                                        key={email.inboundId}
                                        tabIndex={0}
                                        onClick={() => setSelectedId(email.inboundId)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSelectedId(email.inboundId);
                                            }
                                        }}
                                        className="cursor-pointer hover:bg-slate-50"
                                    >
                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-700 sm:pl-6">
                                            {formatLocalDateTime(email.receivedAt)}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                                            <StatusBadge status={email.status} />
                                        </td>
                                        <td className="max-w-[180px] truncate px-3 py-4 text-sm text-gray-700">
                                            {email.fromEmail || '—'}
                                        </td>
                                        {tab === 'custom' && (
                                            <td className="max-w-[140px] truncate px-3 py-4 font-mono text-xs text-gray-500">
                                                {email.inboxId || email.recipientLocalPart || '—'}
                                            </td>
                                        )}
                                        <td className="max-w-[220px] truncate px-3 py-4 text-sm text-gray-700">
                                            {email.subject || <span className="text-slate-400">(no subject)</span>}
                                        </td>
                                        {showExtractionColumns && (
                                            <>
                                                <td className="max-w-[120px] truncate px-3 py-4 font-mono text-xs text-gray-500">
                                                    {email.uid || '—'}
                                                </td>
                                                <td className="max-w-[140px] truncate px-3 py-4 text-sm">
                                                    {email.extractionId ? (
                                                        <MonoLink href={extractionHref}>{email.extractionId}</MonoLink>
                                                    ) : '—'}
                                                </td>
                                                <td className="max-w-[140px] truncate px-3 py-4 text-sm">
                                                    {email.batchId ? (
                                                        <MonoLink href={batchHref}>{email.batchId}</MonoLink>
                                                    ) : '—'}
                                                </td>
                                            </>
                                        )}
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                                            {email.attachments.length}
                                            {email.rejectedAttachments.length > 0 && (
                                                <span className="ml-2 text-xs font-medium text-red-600">
                                                    {email.rejectedAttachments.length} rejected
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Transition.Root show={Boolean(selectedEmail)} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeDetail}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-4xl">
                                    {selectedEmail && (
                                        <>
                                            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
                                                <div className="min-w-0">
                                                    <Dialog.Title className="truncate text-base font-semibold text-slate-900">
                                                        {selectedEmail.subject || '(no subject)'}
                                                    </Dialog.Title>
                                                    <p className="mt-1 font-mono text-xs text-slate-400">{selectedEmail.inboundId}</p>
                                                </div>
                                                <div className="flex flex-shrink-0 items-center gap-3">
                                                    <StatusBadge status={selectedEmail.status} />
                                                    <button
                                                        type="button"
                                                        onClick={closeDetail}
                                                        className="rounded-md text-gray-400 hover:text-gray-500"
                                                    >
                                                        <span className="sr-only">Close</span>
                                                        <XMarkIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="max-h-[80vh] overflow-y-auto px-6 py-5">
                                                <EmailDetail
                                                    email={selectedEmail}
                                                    showExtractionLinks={
                                                        selectedEmail.kind === 'extraction'
                                                        || Boolean(selectedEmail.extractionId)
                                                    }
                                                />
                                            </div>
                                        </>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
}
